import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DatePicker, Segmented, Select, Space, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import type { ReportParams } from '@/services/reportDocuments.service';
import ReportDocumentPanel from './ReportDocumentPanel';
import BranchFilterSelect from './BranchFilterSelect';
import PeriodRangeFilter from './PeriodRangeFilter';
import { isoDate } from './periodPresets';

const { Text } = Typography;

export type FinancialStatementKey = 'trial-balance' | 'profit-loss' | 'balance-sheet' | 'cash-flow';
type Compare = 'none' | 'prior_period' | 'prior_year';
type CashFlowView = 'summary' | 'detail';

const AS_OF_REPORTS: FinancialStatementKey[] = ['balance-sheet'];

/** Trial Balance / P&L / Balance Sheet / Cash Flow on the report document standard. */
const FinancialStatementReport: React.FC<{ reportKey: FinancialStatementKey }> = ({ reportKey }) => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const asOfMode = AS_OF_REPORTS.includes(reportKey);
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('year'), dayjs()]);
  const [asOf, setAsOf] = useState<Dayjs>(dayjs());
  const [branchId, setBranchId] = useState<string | undefined>();
  const [compare, setCompare] = useState<Compare>('none');
  const [view, setView] = useState<CashFlowView>('summary');

  const compareOptions: Array<{ value: Compare; label: string }> = asOfMode
    ? [
        { value: 'none', label: t('noComparative') },
        { value: 'prior_period', label: t('previousMonthEnd') },
        { value: 'prior_year', label: t('sameDateLastYear') },
      ]
    : [
        { value: 'none', label: t('noComparative') },
        { value: 'prior_period', label: t('previousPeriod') },
        { value: 'prior_year', label: t('samePeriodLastYear') },
      ];

  const buildParams = (overrides?: Partial<{ view: CashFlowView }>): ReportParams => {
    const p: ReportParams = asOfMode ? { as_of: isoDate(asOf) } : { from: isoDate(range[0]), to: isoDate(range[1]) };
    if (branchId) p.branch_id = branchId;
    if (compare !== 'none') p.compare = compare;
    if (reportKey === 'cash-flow') p.view = overrides?.view ?? view;
    return p;
  };
  const [params, setParams] = useState<ReportParams | null>(() => buildParams());

  const filters = (
    <Space wrap size="middle" align="end">
      {asOfMode ? (
        <Space direction="vertical" size={4}>
          <Text type="secondary">{t('asAt')}</Text>
          <DatePicker value={asOf} format="DD-MM-YYYY" allowClear={false} onChange={(d) => d && setAsOf(d)} />
        </Space>
      ) : (
        <PeriodRangeFilter value={range} onChange={setRange} initialPreset="ytd" />
      )}
      <Space direction="vertical" size={4}>
        <Text type="secondary">{t('branch')}</Text>
        <BranchFilterSelect value={branchId} onChange={setBranchId} />
      </Space>
      <Space direction="vertical" size={4}>
        <Text type="secondary">{t('comparative')}</Text>
        <Select<Compare> style={{ width: 200 }} value={compare} onChange={setCompare} options={compareOptions} />
      </Space>
      {reportKey === 'cash-flow' && (
        <Segmented<CashFlowView>
          value={view}
          onChange={(v) => {
            setView(v);
            setParams(buildParams({ view: v }));
          }}
          options={[
            { value: 'summary', label: t('summary') },
            { value: 'detail', label: t('voucherDetail') },
          ]}
        />
      )}
      <Button type="primary" icon={<SearchOutlined />} onClick={() => setParams(buildParams())}>
        {tc('generate')}
      </Button>
    </Space>
  );

  return <ReportDocumentPanel reportKey={reportKey} params={params} filters={filters} />;
};

export default FinancialStatementReport;
